import { type RefObject, useEffect, useState } from 'react'

import type {
  DirectoryAccount,
  DirectoryAccountDetailsDTO,
} from '@/modules/projects/stores/types'
import type { LaneActions } from './AccountLaneHeader'
import { type AccountDetails, accountOf, firstNumberOf } from './accounts'
import { accountEditRequests } from './editRequests'
import { segmentIdOf } from './fromProject'
import type { Account, DatePeriod } from './types'
import type { ConfirmedEdits } from './useConfirmedEdits'

// lukita keeps the type as an upcased enum key, the editor as the lower-case word.
const accountBody = (
  details: AccountDetails,
  last4: string,
): DirectoryAccountDetailsDTO => ({
  last4,
  account_type: details.accountType?.toUpperCase() ?? null,
  owner: details.owner,
  institution: details.institution,
})

interface FactEditsInput {
  accounts: Account[]
  directory: DirectoryAccount[]
  openId: string
  edits: ConfirmedEdits
  mapRef: RefObject<HTMLDivElement | null>
}

// Corrections to the facts on a lane or the open statement: the account itself, which account the
// statement is filed under, and its dates.
export const useFactEdits = ({
  accounts,
  directory,
  openId,
  edits,
  mapRef,
}: FactEditsInput) => {
  const [revealNumber, setRevealNumber] = useState<string | null>(null)

  const directoryEntryFor = (last4: string) =>
    directory.find((entry) => entry.last4 === last4)

  // One edit for the whole lane: a digit misread on a cover page was misread on every cover page,
  // so lukita relabels every row carrying the old number. A combined mailing sends one per number.
  const saveAccount = (account: Account, details: AccountDetails) =>
    edits.propose(
      accountEditRequests(
        directoryEntryFor,
        account.numbers,
        details.numbers.map((number) => accountBody(details, number)),
      ),
    )

  // The add dialog is the question itself: it shows the account being added, and nothing else moves.
  const addAccount = async (details: AccountDetails) => {
    await edits.commit(
      details.numbers.map((number) => ({
        kind: 'add_account' as const,
        account: accountBody(details, number),
      })),
    )
    setRevealNumber(details.numbers[0] ?? null)
  }

  const removeAccount = (accountId: string) => {
    const account = accountOf(accounts, accountId)
    edits.propose(
      (account?.numbers ?? []).flatMap((number) => {
        const entry = directoryEntryFor(number)
        return entry
          ? [{ kind: 'remove_account' as const, account_id: entry.id }]
          : []
      }),
    )
  }

  // Re-filing a statement is a label, not a move: its pages stay where they are and lukita
  // restamps the rows it already read. A slip of the picker once put a checking statement on a
  // card, so the move is confirmed against what the statement's own pages say.
  const fileUnderAccount = (accountId: string) => {
    const segmentId = segmentIdOf(openId)
    const last4 = firstNumberOf(accounts, accountId)
    if (segmentId === null || !last4) return
    edits.propose([{ kind: 'refile_statement', segment_id: segmentId, last4 }])
  }

  // A number the machine never read. It is added to the directory, then the move of the open
  // statement is confirmed like any other, because that is where the user is standing.
  const addAccountAndFileHere = async (details: AccountDetails) => {
    const segmentId = segmentIdOf(openId)
    const last4 = details.numbers[0]
    if (segmentId === null || !last4) return
    await edits.commit([
      { kind: 'add_account', account: accountBody(details, last4) },
    ])
    setRevealNumber(last4)
    edits.propose([{ kind: 'refile_statement', segment_id: segmentId, last4 }])
  }

  const setPeriod = (period: DatePeriod) => {
    const segmentId = segmentIdOf(openId)
    if (segmentId === null) return
    edits.propose([
      {
        kind: 'change_statement_period',
        segment_id: segmentId,
        start_date: period.start,
        end_date: period.end,
      },
    ])
  }

  // The new lane lands at the bottom of the map once lukita answers, so the map goes to it.
  useEffect(() => {
    if (revealNumber === null) return
    const account = accounts.find((candidate) =>
      candidate.numbers.includes(revealNumber),
    )
    if (!account) return
    mapRef.current
      ?.querySelector(`[data-lane-label="${account.id}"]`)
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    setRevealNumber(null)
  }, [revealNumber, accounts, mapRef])

  const laneActions: LaneActions = {
    onEditAccount: (accountId, details) => {
      const account = accountOf(accounts, accountId)
      if (account) saveAccount(account, details)
    },
    onRemoveAccount: removeAccount,
  }

  return {
    laneActions,
    addAccount,
    addAccountAndFileHere,
    fileUnderAccount,
    setPeriod,
  }
}
